import {ClientProject} from '../../../types/project-type/projectDetailsType';
import AurHighlyConfidential from '../aur-highly-confidential/AurHighlyConfidential';
import {useState, useEffect} from 'react';
import {AurOptionProps} from '../../../types/types';
import {t} from 'i18next';
import {Box, SelectChangeEvent, Typography} from '@mui/material';
import {ProjectDetails} from '../../pages/user-management/models';
import AurServerSingleSelect from '../aur-select-dropdown/AurServerSingleSelect';
import {Option} from '../aur-select/AurSelect';
import HostCountryDetails from '../../aur-host-country-details/AurHostCountryDetails';

type AurProjectSelectProps = {
  clientProjects: ClientProject[];
  clientHCProjects: ClientProject[];
  unsavedProjects: ProjectDetails[];
  isAdmin?: boolean;
  showHCProjects?: boolean;
  disabled?: boolean;
  projectItemsVisible?: number;
  anchorBottonPosition?: boolean;
  field: {
    value: string | undefined;
    onChange: (event: SelectChangeEvent<string>) => void;
    onBlur?: () => void;
  };
  fieldState: {
    error?: {
      message?: string;
    };
  };
};

type AurProjectOptionProps = AurOptionProps & {
  highlyConfidential?: boolean;
  domainCountryCode?: string;
  dataCenterName?: string;
  countryName?: string;
};

const AurProjectSelect = (props: AurProjectSelectProps) => {
  const HC_PROJECT_ITEM_HEIGHT = 118;
  const [projectList, setProjectList] = useState<AurProjectOptionProps[]>([]);
  const [unsavedProjectIds, setUnsavedProjectIds] = useState<string[]>([]);
  const [selectedProject, setSelectedProject] =
    useState<AurProjectOptionProps>();

  useEffect(() => {
    setUnsavedProjectIds(
      props.unsavedProjects.map((x) => x.projectId.toLowerCase())
    );
  }, [props.unsavedProjects]);

  useEffect(() => {
    if (props.showHCProjects) {
      if (props.isAdmin)
        setProjectList(formatProjectOptions(props.clientHCProjects));
      else setProjectList(formatProjectOptions(props.clientProjects));
    } else {
      setProjectList(
        formatProjectOptions(
          props.clientProjects.filter((project) => !project.highlyConfidential)
        )
      );
    }
  }, [
    props.showHCProjects,
    props.isAdmin,
    props.clientHCProjects,
    props.clientProjects,
    unsavedProjectIds,
  ]);

  const selectItemWithHostCountryDetails = (project: ClientProject) => (
    <Box>
      <Typography className="project-title typo-text subhead4_size">
        {project.projectName}
      </Typography>
      {project.highlyConfidential ? (
        <AurHighlyConfidential size="small" type="pill" />
      ) : (
        <></>
      )}
      {<HostCountryDetails {...project} />}
    </Box>
  );

  const formatProjectOptions = (projects: ClientProject[]) => {
    if (unsavedProjectIds.length > 0) {
      if (props.field.value !== '' && props.field.value !== undefined) {
        const otherProjects = unsavedProjectIds.filter(
          (x) => x !== props.field.value
        );
        projects = projects.filter(
          (x) => !otherProjects.includes(x.projectId.toLowerCase())
        );
      } else {
        projects = projects.filter(
          (x) => !unsavedProjectIds.includes(x.projectId.toLowerCase())
        );
      }
    }

    return projects.map((project: ClientProject) => {
      if (project.highlyConfidential) {
        return {
          key: project.projectId.toLowerCase(),
          value: project.projectId.toLowerCase(),
          label: project.projectName,
          displayElement: selectItemWithHostCountryDetails(project),
          height: HC_PROJECT_ITEM_HEIGHT,
          ...project,
        };
      } else {
        return {
          key: project.projectId.toLowerCase(),
          value: project.projectId.toLowerCase(),
          label: project.projectName,
          displayElement: selectItemWithHostCountryDetails(project),
          height: 90,
          ...project,
        };
      }
    });
  };

  const transformOnChange = (
    onChange: (event: SelectChangeEvent<string>) => void
  ) => {
    return (selectedOption: Option) => {
      const mEvent = {
        target: {value: selectedOption.value},
      } as SelectChangeEvent<string>;

      onChange(mEvent);
    };
  };
  const transformedField = {
    ...props.field,
    label:
      props.clientHCProjects.find(
        (x) => x.projectId.toLowerCase() === props.field.value?.toLowerCase()
      )?.projectName ||
      props.clientProjects.find(
        (x) => x.projectId.toLowerCase() === props.field.value?.toLowerCase()
      )?.projectName ||
      '',
    onChange: transformOnChange(props.field.onChange),
  };
  const searchProject = async (searchTerm: string): Promise<Option[]> => {
    try {
      const result = projectList ?? [];
      const filteredResults = result.filter((item) =>
        item.label.toLowerCase().includes(searchTerm.toLowerCase())
      );
      return filteredResults.map((item) => ({
        key: item.key,
        value: item.key,
        label: item.label,
      }));
    } catch (e) {
      console.error('Error', e);
      return [];
    }
  };

  useEffect(() => {
    if (!props.field.value) {
      setSelectedProject(undefined);
    }
  }, [props.field.value]);

  return (
    <>
      <AurServerSingleSelect
        optionList={projectList ?? []}
        searchable
        onSearch={searchProject}
        helperText={
          props.disabled ? t('addEditTeamMember.form.projectHelperText') : ''
        }
        dataTestId="aur-test-project-dropdown"
        label={
          projectList.length === 0 && !props.disabled
            ? t('addEditTeamMember.form.noProjects')
            : t('addEditTeamMember.form.projects')
        }
        aria-label={
          projectList.length === 0
            ? t('addEditTeamMember.form.noProjects')
            : t('addEditTeamMember.form.projects')
        }
        labelId={
          projectList.length === 0
            ? "project-select-label"
            : t('addEditTeamMember.form.projects')
        }
        field={transformedField}
        fieldState={props.fieldState}
        isVariableHeight
        itemsVisible={props.projectItemsVisible ?? 3}
        anchorBottonPosition={props.anchorBottonPosition}
        onSelect={(item) => {
          setSelectedProject(item);
        }}
        disabled={props.disabled}
        customProjectClassName="project-dropdown-container"
      />

      {selectedProject?.highlyConfidential ? (
        <AurHighlyConfidential
          className="hc-project-warning no-bottom-margin"
          size="small"
          type="pill"
          showWarningText={true}
        />
      ) : (
        <></>
      )}

      {selectedProject ? <HostCountryDetails {...selectedProject} /> : <></>}
    </>
  );
};

export default AurProjectSelect;
